/**
 * FieldLock — a "these fields are read-only" scope that the shared inputs read.
 *
 * Wrap fields in `<FieldLockScope locked>` and every shared input inside it
 * (Input, Textarea, Select, SearchableCombobox, SearchableMultiSelect,
 * CalendarDatePicker / DatePicker, Switch, Checkbox, the rich-text editors)
 * shows its value and refuses a change, with no disabled (greyed) styling and
 * no text saying so. A nested `<FieldLockScope locked={false}>` turns its own
 * fields back on, which is how a page lets a few fields stay editable inside
 * an otherwise locked form.
 *
 * Why a context and not `<fieldset disabled>`: a disabled fieldset cannot
 * re-enable a nested control, it greys every native control it disables, and
 * it also disables buttons that only read (tabs, image previews, expanders).
 *
 * Outside any scope `useFieldLocked()` is false, so every input behaves
 * exactly as it always has.
 */
import { createContext, useContext, type ReactNode } from 'react';

const FieldLockContext = createContext(false);

export function FieldLockScope({ locked, children }: { locked: boolean; children: ReactNode }) {
  return <FieldLockContext.Provider value={locked}>{children}</FieldLockContext.Provider>;
}

/** True inside a locked FieldLockScope: show the value, accept no change, don't look disabled. */
export function useFieldLocked(): boolean {
  return useContext(FieldLockContext);
}
